import { useEffect, useRef, useState } from "react";
import { firstVisit } from "@/mocks/home";

export default function SectionFirstVisit() {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onScroll = () => {
      const rect = el.getBoundingClientRect();
      const total = el.offsetHeight - window.innerHeight;
      const passed = Math.min(Math.max(-rect.top, 0), total);
      const p = total > 0 ? passed / total : 0;
      const idx = Math.min(firstVisit.length - 1, Math.floor(p * firstVisit.length));
      setActive(idx);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const cur = firstVisit[active];

  return (
    <section id="first-visit" className="relative bg-page-cool">
      <div className="w-full px-6 md:px-10 pt-24 md:pt-32">
        <div className="max-w-[1440px] mx-auto">
          <div className="text-ds-eyebrow text-ink-muted mb-space-ds-intro-eyebrow-heading">
            05 · First Visit
          </div>
          <h2 className="text-ds-feature font-medium text-ink max-w-[720px]">
            Your first visit,<br />
            <span className="font-serif-italic">clearly explained.</span>
          </h2>
        </div>
      </div>

      <div ref={wrapRef} style={{ height: `${firstVisit.length * 100}vh` }} className="relative">
        <div className="sticky top-0 h-screen flex flex-col justify-center">
          <div className="w-full px-6 md:px-10">
            <div className="max-w-[1440px] mx-auto">
              <div className="relative w-full h-[60vh] md:h-[64vh] rounded-editorial overflow-hidden bg-white">
                {firstVisit.map((s, i) => (
                  <img
                    key={s.id}
                    src={s.image}
                    alt={s.step}
                    className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700 ${i === active ? "opacity-100" : "opacity-0"}`}
                  />
                ))}
                <div className="absolute left-6 md:left-10 bottom-6 md:bottom-10 max-w-[420px]">
                  <div className="inline-block px-3 py-1 rounded-full bg-white/90 backdrop-blur text-ds-eyebrow tracking-[0.2em] text-brand">
                    {cur.step}
                  </div>
                  <div className="mt-3 text-ds-content-title text-white drop-shadow-md">
                    {cur.title}
                  </div>
                  <div className="mt-2 text-ds-meta text-white/90">
                    {cur.copy}
                  </div>
                </div>
              </div>

              {/* Progress line */}
              <div className="mt-6 flex items-center gap-3 md:gap-6 overflow-x-auto no-scrollbar">
                {firstVisit.map((s, i) => {
                  const done = i <= active;
                  return (
                    <div key={s.id} className="flex items-center gap-3 md:gap-6 flex-shrink-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-ds-meta font-medium transition ${done ? "text-brand" : "text-ink-faint"}`}>
                          {s.id}
                        </span>
                        <span className={`text-ds-meta transition ${done ? "text-ink" : "text-ink-faint"}`}>
                          {s.step}
                        </span>
                      </div>
                      {i < firstVisit.length - 1 && (
                        <div className="w-12 md:w-24 h-px bg-gray-300 relative overflow-hidden">
                          <div className={`absolute inset-y-0 left-0 bg-brand transition-all duration-500 ${done ? "w-full" : "w-0"}`} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}